import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Pencil, Printer, Trash2, FileCheck } from "lucide-react";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import React, { Dispatch, SetStateAction } from "react";
import { useToast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import { Transaction } from "@/types/pos/transaction";
import { useNavigate } from "react-router-dom";
import warning from "@/assets/warning.svg";
import { setInvoice } from "@/redux/invoiceSlice";
import { useDispatch } from "react-redux";
import { InvoiceExport, ReceiptExport } from "@/components/admin/pos/sales_history/table/receipt-component";
import { InvoiceDetails } from "@/types/invoice";
import { useUpdateInvoiceMutation, useSendInvoiceByEmailMutation, useCancelInvoiceMutation, useCreatePendingTransactionMutation } from "@/services/invoiceApi";
import { InvoiceStatus, InvoiceType } from "@/types/enums";
import { useLazyGetTransactionByIdQuery } from "@/services/transactionApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

export function DataTableRowActions({
  data,
  refetch,
  handleEdit,
  access,
}: {
  data: InvoiceDetails;
  refetch: () => void;
  handleEdit: (payload: InvoiceDetails) => void;
  access: string;
}) {
  
  const cancel_paid_invoices = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .hide_cancel_invoice ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const [isdelete, setIsDelete] = React.useState(false);
  const [sendInvoiceByEmail] = useSendInvoiceByEmailMutation()
  const [updateInvoice] = useUpdateInvoiceMutation()
  const [cancelInvoice] = useCancelInvoiceMutation()
  const [createPendingTransaction, { isLoading: isConverting }] = useCreatePendingTransactionMutation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use lazy query for transaction data - only call when needed
  const [getTransactionById, { isLoading: isLoadingTransaction }] =
    useLazyGetTransactionByIdQuery();

  async function buildPrintHtml(currentTransactionData?: Transaction): Promise<string> {
    const isInvoice = String(data.invoice_type).toLowerCase() === "invoice";
    const transaction = currentTransactionData;

    if (
      isInvoice &&
      data.status === InvoiceStatus.Paid &&
      data.transaction_id != null
    ) {
      const isInvoiceContribution = data.items?.some(
        (item: any) => String(item?.revenue_type).toLowerCase() === "donation",
      );
      return await ReceiptExport(
        transaction as Transaction,
        isInvoiceContribution,
      );
    }

    return await InvoiceExport(
      data,
      isInvoice ? transaction?.payments : undefined,
      isInvoice ? transaction?.due_date : undefined,
    );
  }

  function printHtml(htmlContent: string) {
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0px";
    iframe.style.height = "0px";
    iframe.style.border = "none";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document || iframe.contentDocument;
    if (!iframeDoc) return;
    iframeDoc.open();
    iframeDoc.write(htmlContent || "");
    iframeDoc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 100);
    };
  }

  const handlePrint = async () => {
    try {
      let currentTransactionData: Transaction | undefined;

      const isInvoice = String(data.invoice_type).toLowerCase() === "invoice";

      // Invoice prints may include payment details from the linked transaction.
      if (isInvoice && data.transaction_id != null) {
        // Fetch transaction data
        const result = await getTransactionById({
          transaction_id: data.transaction_id,
          org_id: orgId,
        });

        if (result.error) {
          toast({
            variant: "destructive",
            title: "Error Loading Transaction",
            description: "Failed to load transaction data. Please try again.",
          });
          return;
        }

        // Use the fetched data
        currentTransactionData = result.data;
      }

      // Generate and print the HTML
      const html = await buildPrintHtml(currentTransactionData);
      printHtml(html);
    
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Print Error",
        description: "Failed to generate print content. Please try again.",
      });
    }
  };

  const handleEmail = async () => {
    try {
      const resp = await sendInvoiceByEmail(data.id as number).unwrap();
      if (resp) {
        console.log({ resp });
        toast({
          variant: "success",
          title: "Invoice Sent Successfully",
        });
        refetch();
      }
      return;
    } catch (error) {
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  
  const handleCreatePendingTransaction = async () => {
    try {
      const resp = await createPendingTransaction(data.id as number).unwrap();
      if (resp) {
        toast({
          variant: "success",
          title: "Converted Successfully",
          description: "Invoice converted to pending and transaction created.",
        });
        refetch();
      }
    } catch (error) {
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Conversion",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message || "Failed to convert invoice."}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Conversion",
          description: "Something Went Wrong.",
        });
      }
    }
  };

  const handlePay = () => {
    dispatch(setInvoice(data))

    setTimeout(() => {
      navigate('/counter-selection')
    }, 500)
  }

  const deleteRow = async () => {
    
    try {
      if ([InvoiceStatus.Paid, InvoiceStatus.Processing].includes(data.status)) {
        await cancelInvoice(data.id as number).unwrap();

        toast({
          variant: "success",
          title: "Invoice Cancelled Successfully",
        });
      } else {
        await updateInvoice({ ...data, invoice_id: data.id, status: InvoiceStatus.Cancelled }).unwrap();

        toast({
          variant: "success",
          title: "Invoice Cancelled Successfully",
        });
      }

      refetch();
    } catch (error) {
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error",
          description:
            typedError.data?.detail ||
            (typedError.data as { message?: string }).message,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const isPending =
    data.status === InvoiceStatus.Pending ||
    String(data.status).toLowerCase() === "pending";
  const isInvoice =
    data.invoice_type === InvoiceType.Invoice ||
    String(data.invoice_type).toLowerCase() === "invoice";

  const canEditOrPay =
    data.status !== InvoiceStatus.Paid &&
    data.status !== InvoiceStatus.Refunded &&
    data.status !== InvoiceStatus.Cancelled &&
    !(isPending && isInvoice);

  return (
    <>
      <Dialog>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
            >
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className=" text-lg" onClick={(e) => e.stopPropagation()}>
            {Boolean(
              (data.invoice_type === InvoiceType.Proforma || String(data.invoice_type)?.toLowerCase() === "proforma") &&
              !data.transaction_id &&
              data.status !== InvoiceStatus.Paid &&
              data.status !== InvoiceStatus.Cancelled &&
              data.status !== InvoiceStatus.Refunded
            ) && (
              <DropdownMenuItem
                className="text-nowrap"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreatePendingTransaction();
                }}
                disabled={isConverting}
              >
                {isConverting ? (
                  <i className="fa fa-spinner fa-spin mr-2 h-4 w-4"></i>
                ) : (
                  <FileCheck className="mr-2 h-4 w-4" />
                )}
                {isConverting ? "Converting..." : "Create Transaction"}
              </DropdownMenuItem>
            )}
            <DialogTrigger asChild>
              {canEditOrPay && data.items.some((item: any) => item.item_type !== "facilities") && <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                handleEdit(data)
              }}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>}
            </DialogTrigger>

            <DropdownMenuItem
              className="text-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                handleEmail()
              }}
            >
              <i className="fa-solid fa-envelope mr-2 h-4 w-4"></i>
              Email
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                handlePrint()
              }}
              disabled={isLoadingTransaction}
            >
              {isLoadingTransaction ? (
                <i className="fa fa-spinner fa-spin mr-2 h-4 w-4"></i>
              ) : (
                <Printer className="mr-2 h-4 w-4" />
              )}
              {isLoadingTransaction ? "Loading..." : "Print"}
            </DropdownMenuItem>

            {canEditOrPay && <DropdownMenuItem
              className="text-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                handlePay()
              }}
            >
              <Printer className="mr-2 h-4 w-4" />
              Pay
            </DropdownMenuItem>}
            {
            access === "full_access" &&
            data.status !== InvoiceStatus.Refunded &&
            data.status !== InvoiceStatus.Cancelled &&
            (
              // Case 1: Unpaid → allow for everyone
              data.status !== InvoiceStatus.Paid ||

              // Case 2: Paid → require special permission
              cancel_paid_invoices === "full_access"
            ) && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();

                  if (data.status == InvoiceStatus.Cancelled) {
                    toast({
                      variant: "destructive",
                      title: "Invoice is already cancelled",
                    });
                    return;
                  }

                  setIsDelete(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Cancel
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </Dialog >
      {isdelete && (
        <AlertDialog open={isdelete} onOpenChange={setIsDelete}>
          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-center  justify-center gap-4">
                  <img src={warning} alt="warning" className="w-18 h-18" />
                  <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                    Are you sure to cancel this invoice, this action cannot be
                    reverted?
                  </AlertDialogTitle>
                </div>
                <div className="w-full flex justify-between items-center gap-3 mt-4">
                  <AlertDialogCancel
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDelete(false)
                    }}
                    className="w-full border border-primary font-semibold"
                  >
                    <i className="fa fa-xmark text-base px-1 "></i>
                    No
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRow()
                    }}
                    className="w-full bg-primary !text-black font-semibold"
                  >
                    <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                    Yes
                  </AlertDialogAction>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )
      }
    </>
  );
}